import { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { useNavigate } from 'react-router-dom';
import WebScrollView from '../components/WebScrollView';
import { Calendar, ArrowLeft, CheckCircle2, ChevronDown, Info, ArrowRight } from '../lib/icons';
import { supabase } from '../lib/supabase';
import { shadows } from '../lib/styles';
import { useActiveHome } from '../contexts/ActiveHomeContext';

interface WalkthroughAvailability {
  id: string;
  available_date: string;
  available_time_slots: string[];
  max_bookings_per_slot: number;
  home_manager: string;
  market_id: string | null;
  market_name?: string | null;
  market_walkthrough_type?: 'in_person' | 'virtual' | null;
  booked_counts_by_slot?: Record<string, number> | null;
}

interface Home {
  id: string;
  address: string;
  city: string;
  state: string;
  market_id: string | null;
}

const ScheduleWalkthrough = () => {
  const { homes: accessibleHomes, activeHome, setActiveHomeById } = useActiveHome();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availability, setAvailability] = useState<WalkthroughAvailability[]>([]);
  const [displayedCount, setDisplayedCount] = useState(10);
  const [homes, setHomes] = useState<Home[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const [selectedHomeId, setSelectedHomeId] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [accessibleHomes, activeHome?.id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/login');
        return;
      }

      const homesData = (accessibleHomes || []) as Home[];
      setHomes(homesData);
      if (homesData.length > 0) {
        setSelectedHomeId(activeHome?.id || homesData[0].id);
      }

      // Prefer the home the user is scheduling for (step 2 selection) over global active home
      const homeIdForAvailability =
        selectedHomeId ||
        activeHome?.id ||
        (homesData.length > 0 ? homesData[0].id : '');
      const currentHome =
        homesData.find((home) => home.id === homeIdForAvailability) ||
        (homesData.length > 0 ? homesData[0] : null);
      let homeMarketId = currentHome?.market_id;

      // If home has no market, get the default market
      if (!homeMarketId) {
        const { data: defaultMarket, error: defaultMarketError } = await supabase
          .from('markets')
          .select('id')
          .eq('is_default', true)
          .eq('is_active', true)
          .single();

        if (!defaultMarketError && defaultMarket) {
          homeMarketId = defaultMarket.id;
        }
      }

      // Fetch available walkthrough dates - filter by market (home's market or default)
      let availabilityQuery = supabase
        .from('walkthrough_availability_with_bookings')
        .select('id, available_date, available_time_slots, max_bookings_per_slot, home_manager, market_id, booked_counts_by_slot, market_name, market_walkthrough_type')
        .eq('is_active', true)
        .gte('available_date', new Date().toISOString().split('T')[0]);

      // Filter by market if we have one (home's market or default)
      if (homeMarketId) {
        availabilityQuery = availabilityQuery.eq('market_id', homeMarketId);
      }

      const { data: availabilityData, error: availabilityError } = await availabilityQuery
        .order('available_date', { ascending: true });

      if (availabilityError) throw availabilityError;
      
      // Parse booked_counts_by_slot if it's a JSON string
      const processedData = (availabilityData || []).map(item => ({
        ...item,
        booked_counts_by_slot: typeof item.booked_counts_by_slot === 'string' 
          ? JSON.parse(item.booked_counts_by_slot) 
          : item.booked_counts_by_slot
      }));
      
      setAvailability(processedData);
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  const handleSchedule = async () => {
    if (!selectedDate || !selectedTimeSlot || !selectedHomeId) {
      setError('Please select a date, time, and home');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Combine date and time into scheduled_date
      const [hours, minutes, period] = selectedTimeSlot.match(/(\d+):(\d+)\s*(AM|PM)/i)?.slice(1) || [];
      if (!hours || !minutes || !period) {
        throw new Error('Invalid time format');
      }

      let hour24 = parseInt(hours);
      const min = parseInt(minutes);
      if (period.toUpperCase() === 'PM' && hour24 !== 12) {
        hour24 += 12;
      } else if (period.toUpperCase() === 'AM' && hour24 === 12) {
        hour24 = 0;
      }

      // Parse YYYY-MM-DD as local date components to avoid UTC-midnight drift
      const [selYear, selMonth, selDay] = selectedDate.split('-').map(Number);
      const scheduledDate = new Date(selYear, selMonth - 1, selDay, hour24, min, 0, 0);

      // Get the home manager from the selected availability
      // Find availability that matches the date AND contains the selected time slot
      const selectedAvailability = availability.find(a => 
        a.available_date === selectedDate && 
        a.available_time_slots.includes(selectedTimeSlot)
      );
      
      // Fallback to just matching by date if time slot match fails
      const availabilityByDate = selectedAvailability || availability.find(a => a.available_date === selectedDate);
      
      const homeManager = availabilityByDate?.home_manager;
      
      // Only assign if home_manager exists and is not "Unassigned"
      const assignedTo = homeManager && homeManager !== 'Unassigned' ? homeManager : null;

      console.log('Scheduling walkthrough:', {
        selectedDate,
        selectedTimeSlot,
        selectedAvailabilityId: availabilityByDate?.id,
        selectedAvailabilityFull: availabilityByDate,
        homeManager,
        assignedTo,
        userId: user.id,
        allAvailabilityForDate: availability.filter(a => a.available_date === selectedDate)
      });

      // Create the walkthrough visit
      const visitInsertData = {
        home_id: selectedHomeId,
        visit_type: 'walkthrough',
        status: 'scheduled',
        scheduled_date: scheduledDate.toISOString(),
        assigned_to: assignedTo,
        created_by: user.id,
        updated_by: user.id
      };
      
      console.log('Inserting visit with data:', visitInsertData);

      const { data: visitData, error: visitError } = await supabase
        .from('visits')
        .insert([visitInsertData])
        .select()
        .single();

      if (visitError) {
        console.error('Error creating visit:', visitError);
        throw visitError;
      }
      
      console.log('Visit created successfully:', visitData);

      // Show confirmation screen
      setCurrentStep(3);
    } catch (err: any) {
      console.error('Error scheduling walkthrough:', err);
      setError(err.message || 'Failed to schedule walkthrough');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    // Parse YYYY-MM-DD as local date, not UTC, to avoid timezone off-by-one issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const displayedAvailability = availability.slice(0, displayedCount);
  const hasMore = availability.length > displayedCount;

  const handleLoadMore = () => {
    setDisplayedCount(prev => Math.min(prev + 10, availability.length));
  };

  if (loading) {
    return (
      <WebScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#9ca3af" />
        </View>
      </WebScrollView>
    );
  }

  return (
    <WebScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      {currentStep !== 3 && (
        <View style={styles.header}>
          <Pressable
            onPress={() => navigate(-1)}
            style={styles.backButton}
          >
            <ArrowLeft size={20} color="#111827" />
          </Pressable>
          <Text style={styles.title}>Schedule Your Walkthrough</Text>
        </View>
      )}

      {error && currentStep !== 3 && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      {homes.length === 0 ? (
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>Please add a home first before scheduling a walkthrough.</Text>
          <Pressable
            onPress={() => navigate('/dashboard')}
            style={styles.button}
          >
            <Text style={styles.buttonText}>Go to Dashboard</Text>
          </Pressable>
        </View>
      ) : (
        <>
          {currentStep === 1 ? (
            <>
              {/* Step 1: Walkthrough Explainer */}
              <View style={styles.explainerCard}>
                <View style={styles.explainerHeader}>
                  <View style={styles.explainerIconContainer}>
                    <Info size={24} color="#111827" />
                  </View>
                  <Text style={styles.explainerTitle}>What is a Walkthrough?</Text>
                </View>
                <Text style={styles.explainerText}>
                  A walkthrough is an opportunity for our team member to visit your home, go over our services, and help prioritize projects that matter most to you. During this visit, we'll discuss your home's needs and create a personalized plan.
                </Text>
              </View>

              {/* Continue Button */}
              <Pressable
                onPress={() => setCurrentStep(2)}
                style={styles.continueButton}
              >
                <Text style={styles.continueButtonText}>Continue</Text>
                <ArrowRight size={20} color="#ffffff" />
              </Pressable>
            </>
          ) : currentStep === 3 ? (
            <>
              {/* Step 3: Confirmation */}
              <View style={styles.confirmationCard}>
                <View style={styles.confirmationIconContainer}>
                  <CheckCircle2 size={64} color="#16a34a" />
                </View>
                <Text style={styles.confirmationTitle}>Walkthrough Scheduled!</Text>
                <Text style={styles.confirmationText}>
                  We've received your walkthrough request for {selectedDate && formatDate(selectedDate)} at {selectedTimeSlot}.
                </Text>
                <Text style={styles.confirmationSubtext}>
                  We'll follow up with reminders and confirmations as your walkthrough date approaches. You can view and manage your scheduled walkthroughs from your dashboard.
                </Text>
                <Pressable
                  onPress={() => navigate('/dashboard')}
                  style={styles.confirmationButton}
                >
                  <Text style={styles.confirmationButtonText}>Go to Dashboard</Text>
                </Pressable>
              </View>
            </>
          ) : (
            <>
              {/* Step 2: Date and Time Selection */}
              {/* Home Selection */}
              {homes.length > 1 && (
                <View style={styles.section}>
                  <Text style={styles.sectionTitle}>Select Home</Text>
                  <View style={styles.homeList}>
                    {homes.map((home) => (
                      <Pressable
                        key={home.id}
                        onPress={() => {
                          setSelectedHomeId(home.id);
                          setActiveHomeById(home.id);
                        }}
                        style={[
                          styles.homeOption,
                          selectedHomeId === home.id && styles.homeOptionSelected
                        ]}
                      >
                        <Text style={[
                          styles.homeOptionText,
                          selectedHomeId === home.id && styles.homeOptionTextSelected
                        ]}>
                          {home.address}, {home.city}, {home.state}
                        </Text>
                        {selectedHomeId === home.id && (
                          <CheckCircle2 size={20} color="#16a34a" />
                        )}
                      </Pressable>
                    ))}
                  </View>
                </View>
              )}

              {/* Date and Time Selection */}
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Select Date & Time</Text>
                {availability.length === 0 ? (
                  <View style={styles.emptyState}>
                    <Text style={styles.emptyText}>
                      No available dates at this time. Please check back later or contact support.
                    </Text>
                  </View>
                ) : (
                  <>
                    <View style={styles.dateList}>
                      {displayedAvailability.map((item) => {
                        const isSelected = selectedDate === item.available_date;
                        return (
                          <View key={item.id} style={styles.dateTimeContainer}>
                            <Pressable
                              onPress={() => {
                                setSelectedDate(item.available_date);
                                setSelectedTimeSlot(null);
                              }}
                              style={[
                                styles.dateOption,
                                isSelected && styles.dateOptionSelected
                              ]}
                            >
                              <Calendar size={20} color={isSelected ? "#ffffff" : "#111827"} />
                              <View style={styles.dateOptionContent}>
                                <Text style={[
                                  styles.dateOptionText,
                                  isSelected && styles.dateOptionTextSelected
                                ]}>
                                  {formatDate(item.available_date)}
                                </Text>
                                {item.market_walkthrough_type && (
                                  <Text style={[
                                    styles.walkthroughTypeBadge,
                                    isSelected && styles.walkthroughTypeBadgeSelected
                                  ]}>
                                    {item.market_walkthrough_type === 'virtual' ? '📹 Virtual' : '🏠 In-Person'}
                                  </Text>
                                )}
                              </View>
                              {isSelected && (
                                <CheckCircle2 size={20} color="#ffffff" />
                              )}
                            </Pressable>
                            
                            {/* Time slots appear below selected date */}
                            {isSelected && item.available_time_slots.length > 0 && (
                              <View style={styles.timeSlotsExpanded}>
                                <Text style={styles.timeSlotsLabel}>Select a time:</Text>
                                <View style={styles.timeSlotList}>
                                  {item.available_time_slots.map((slot, index) => {
                                    const bookedCount = item.booked_counts_by_slot?.[slot] || 0;
                                    const isFull = bookedCount >= item.max_bookings_per_slot;
                                    const isPartiallyBooked = bookedCount > 0 && bookedCount < item.max_bookings_per_slot;
                                    return (
                                      <Pressable
                                        key={index}
                                        onPress={() => !isFull && setSelectedTimeSlot(slot)}
                                        disabled={isFull}
                                        style={[
                                          styles.timeSlotOption,
                                          selectedTimeSlot === slot && styles.timeSlotOptionSelected,
                                          isFull && styles.timeSlotOptionFull,
                                          isPartiallyBooked && styles.timeSlotOptionPartial
                                        ]}
                                      >
                                        <Text style={[
                                          styles.timeSlotOptionText,
                                          selectedTimeSlot === slot && styles.timeSlotOptionTextSelected,
                                          isFull && styles.timeSlotOptionTextFull
                                        ]}>
                                          {slot}
                                        </Text>
                                        {bookedCount > 0 && (
                                          <Text style={styles.bookedCountText}>
                                            ({bookedCount}/{item.max_bookings_per_slot})
                                          </Text>
                                        )}
                                        {selectedTimeSlot === slot && !isFull && (
                                          <CheckCircle2 size={20} color="#ffffff" />
                                        )}
                                        {isFull && (
                                          <Text style={styles.fullText}>Full</Text>
                                        )}
                                      </Pressable>
                                    );
                                  })}
                                </View>
                              </View>
                            )}
                          </View>
                        );
                      })}
                    </View>
                    {hasMore && (
                      <Pressable
                        onPress={handleLoadMore}
                        style={styles.loadMoreButton}
                      >
                        <Text style={styles.loadMoreButtonText}>
                          Load More ({availability.length - displayedCount} more available)
                        </Text>
                        <ChevronDown size={16} color="#2563eb" />
                      </Pressable>
                    )}
                  </>
                )}
              </View>

              {/* Schedule Button */}
              {selectedDate && selectedTimeSlot && selectedHomeId && (
                <Pressable
                  onPress={handleSchedule}
                  disabled={saving}
                  style={[styles.scheduleButton, saving && styles.scheduleButtonDisabled]}
                >
                  {saving ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <>
                      <Calendar size={20} color="#ffffff" />
                      <Text style={styles.scheduleButtonText}>Schedule Walkthrough</Text>
                    </>
                  )}
                </Pressable>
              )}
            </>
          )}
        </>
      )}
    </WebScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  contentContainer: {
    padding: 16,
    gap: 24,
    ...(Platform.OS !== 'web' && {
      width: '100%',
    }),
    ...(Platform.OS === 'web' && {
      paddingBottom: 32,
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
    maxWidth: '100%',
  },
  backButton: {
    flexShrink: 0,
    padding: 4,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  title: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    fontSize: 28,
    fontWeight: 'bold',
    color: '#111827',
  },
  loadingContainer: {
    padding: 80,
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorContainer: {
    padding: 12,
    backgroundColor: '#fef2f2',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
  },
  section: {
    gap: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  explainerCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 32,
    gap: 16,
    alignSelf: 'stretch',
    maxWidth: '100%',
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
    }),
  },
  explainerHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    maxWidth: '100%',
  },
  explainerIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#f9fafb',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  explainerTitle: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    fontSize: 24,
    fontWeight: '600',
    color: '#111827',
  },
  explainerText: {
    flexShrink: 1,
    maxWidth: '100%',
    fontSize: 16,
    color: '#4b5563',
    lineHeight: 24,
  },
  continueButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    paddingHorizontal: 24,
    backgroundColor: '#000000',
    borderRadius: 8,
    marginTop: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  continueButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  homeList: {
    gap: 8,
  },
  homeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  homeOptionSelected: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  homeOptionText: {
    fontSize: 16,
    color: '#111827',
  },
  homeOptionTextSelected: {
    color: '#ffffff',
  },
  dateList: {
    gap: 8,
  },
  dateOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    gap: 12,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  dateOptionSelected: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  dateOptionContent: {
    flex: 1,
    gap: 8,
  },
  dateOptionText: {
    fontSize: 16,
    color: '#111827',
  },
  dateOptionTextSelected: {
    color: '#ffffff',
  },
  walkthroughTypeBadge: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  walkthroughTypeBadgeSelected: {
    color: '#e5e7eb',
  },
  dateTimeContainer: {
    gap: 0,
  },
  timeSlotsExpanded: {
    marginTop: 16,
    marginBottom: 12,
    paddingTop: 20,
    paddingBottom: 16,
    paddingHorizontal: 16,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderTopWidth: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  timeSlotsLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 12,
  },
  timeSlotList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 0,
  },
  timeSlotOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    gap: 8,
    minWidth: 120,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  timeSlotOptionSelected: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  timeSlotOptionText: {
    fontSize: 16,
    color: '#111827',
  },
  timeSlotOptionTextSelected: {
    color: '#ffffff',
  },
  timeSlotOptionFull: {
    backgroundColor: '#fef2f2',
    borderColor: '#fecaca',
    opacity: 0.6,
    ...(Platform.OS === 'web' && {
      cursor: 'not-allowed' as any,
    }),
  },
  timeSlotOptionPartial: {
    backgroundColor: '#fef3c7',
    borderColor: '#fbbf24',
  },
  timeSlotOptionTextFull: {
    color: '#9ca3af',
  },
  bookedCountText: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  fullText: {
    fontSize: 12,
    color: '#dc2626',
    fontWeight: '600',
  },
  scheduleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    paddingHorizontal: 24,
    backgroundColor: '#000000',
    borderRadius: 8,
    marginTop: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  scheduleButtonDisabled: {
    opacity: 0.5,
  },
  scheduleButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  confirmationCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 32,
    alignItems: 'center',
    gap: 20,
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
    }),
  },
  confirmationIconContainer: {
    width: 96,
    height: 96,
    borderRadius: 48,
    backgroundColor: '#f0fdf4',
    justifyContent: 'center',
    alignItems: 'center',
  },
  confirmationTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#111827',
    textAlign: 'center',
  },
  confirmationText: {
    fontSize: 16,
    color: '#374151',
    textAlign: 'center',
    lineHeight: 24,
  },
  confirmationSubtext: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 20,
  },
  confirmationButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    backgroundColor: '#000000',
    borderRadius: 8,
    marginTop: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  confirmationButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  emptyState: {
    padding: 32,
    alignItems: 'center',
    gap: 16,
  },
  emptyText: {
    fontSize: 16,
    color: '#6b7280',
    textAlign: 'center',
  },
  button: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  buttonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  loadMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginTop: 8,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#2563eb',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  loadMoreButtonText: {
    color: '#2563eb',
    fontSize: 14,
    fontWeight: '600',
  },
});

export default ScheduleWalkthrough;
